export type MapPlacedModelPayload = {
  name: string;
  alias: string | null;
  desc: string | null;
  obj: string;
  loc: [number, number, number];
  rotation: [number, number, number];
  scale: number;
  min_zoom: number;
  max_zoom: number;
  /** When false, mesh and pick handle are hidden. Default true. */
  visible?: boolean;
  /** Runtime feature instance from a layer binding (not a binding stub). */
  sourceLayer?: string | null;
  /** Persisted binding stub — not rendered as a mesh. */
  layer_bound?: boolean;
  /** Map layer this binding is tied to. */
  layer_name?: string | null;
};

export type MapModelsDocument = {
  objects: MapPlacedModelPayload[];
};

export type MapPlacedModel = MapPlacedModelPayload & {
  id: string;
};

export function createPlacedModelId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function createDefaultPlacedModel(
  obj: string,
  catalogName: string,
  loc: [number, number, number],
): MapPlacedModel {
  return {
    id: createPlacedModelId(),
    name: catalogName,
    alias: null,
    desc: null,
    obj,
    loc,
    // Scene is already ENU-oriented (MapLibre terrain three.js example); keep model local rotation identity by default.
    rotation: [0, 0, 0],
    scale: 1,
    min_zoom: 0,
    max_zoom: 22,
    visible: true,
  };
}

export function toMapModelsDocument(models: MapPlacedModel[]): MapModelsDocument {
  return {
    objects: models.map(({ id: _id, ...payload }) => payload),
  };
}

export function normalizeMapModelsDocument(raw: unknown): MapPlacedModel[] {
  if (!raw) return [];

  let objects: unknown[] = [];
  if (Array.isArray(raw)) {
    objects = raw;
  } else if (typeof raw === 'object') {
    const doc = raw as Record<string, unknown>;
    if (Array.isArray(doc.objects)) objects = doc.objects;
    else if (Array.isArray(doc.models)) objects = doc.models;
    else if (Array.isArray(doc.data)) objects = doc.data;
  }

  return objects
    .map((entry, index): MapPlacedModel | null => {
      if (!entry || typeof entry !== 'object') return null;
      const item = entry as Partial<MapPlacedModelPayload> & { id?: string };
      const obj = item.obj;
      if (!obj) return null;
      return {
        id:
          typeof item.id === 'string' && item.id
            ? item.id
            : createPlacedModelId(),
        name: item.name ?? `Model ${index + 1}`,
        alias: item.alias ?? null,
        desc: item.desc ?? null,
        obj: String(obj),
        loc: Array.isArray(item.loc)
          ? [Number(item.loc[0]), Number(item.loc[1]), Number(item.loc[2] ?? 0)]
          : ([0, 0, 0] as [number, number, number]),
        rotation: Array.isArray(item.rotation)
          ? [Number(item.rotation[0] ?? 0), Number(item.rotation[1] ?? 0), Number(item.rotation[2] ?? 0)]
          : ([0, 0, 0] as [number, number, number]),
        scale: Number(item.scale ?? 1),
        min_zoom: Number(item.min_zoom ?? 0),
        max_zoom: Number(item.max_zoom ?? 22),
        visible: item.visible !== false,
        sourceLayer:
          typeof (item as { sourceLayer?: unknown }).sourceLayer === 'string'
            ? (item as { sourceLayer: string }).sourceLayer
            : null,
        layer_bound: item.layer_bound === true || undefined,
        layer_name:
          typeof item.layer_name === 'string' && item.layer_name
            ? item.layer_name
            : null,
      };
    })
    .filter((item): item is MapPlacedModel => item != null);
}

export const MAP_MODELS_SOURCE_ID = 'geobox-gl-map-models';
export const MAP_MODELS_LAYER_ID = 'geobox-gl-map-models-layer';
export const MAP_MODELS_SELECTED_LAYER_ID = 'geobox-gl-map-models-selected';
export const MAP_MODELS_CUSTOM_LAYER_ID = 'geobox-gl-map-models-3d';
